import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { acquireOperationContext, isSessionTransition, operationRequirement } from '@/session/operation';
import { useEndpoints } from '@/session/useEndpoints';
import { utils } from '@/utils';
import type {
  Integration,
  IntegrationAllowance,
  IntegrationSubscription,
  SalesChannel,
  FulfillmentProvider,
  GeneralIntegration,
} from '@/types/integration';

export type IntegrationsContextType = {
  integrations: Integration[];
  allowances: IntegrationAllowance[];
  sChannels: SalesChannel[] | null;
  fProviders: FulfillmentProvider[] | null;
  generalSubs: GeneralIntegration[] | null;
  subs: IntegrationSubscription[];
  busyFP: boolean;
  busySC: boolean;
  busyGS: boolean;
  refreshIntegrationsData: () => void;
  hasFetched: boolean;
  resetIntegrationsData: () => void;
};

const initialState: IntegrationsContextType = {
  integrations: [],
  allowances: [],
  sChannels: null,
  fProviders: null,
  generalSubs: null,
  subs: [],
  busyFP: false,
  busySC: false,
  busyGS: false,
  refreshIntegrationsData: () => {},
  hasFetched: false,
  resetIntegrationsData: () => {},
};

export const IntegrationsContext = createContext<IntegrationsContextType>(initialState);

const findById = <T extends { id: string | number }>(records: readonly T[], id: unknown): T | undefined => records.find((record) => record.id === id);

export function findSalesChannelById<T extends { id: string | number }>(sChannels: readonly T[], id: unknown): T | undefined {
  if (!Array.isArray(sChannels)) return undefined;
  return findById<T>(sChannels, id);
}

const asArray = <T,>(value: T[] | undefined): T[] => (Array.isArray(value) ? value : []);
const byEnabled = (left: { enabled?: unknown }, right: { enabled?: unknown }) => Number(right.enabled) - Number(left.enabled);
const isCancellation = (error: unknown): boolean => isSessionTransition(error) || utils.error.isAbortError(error);

export const IntegrationsProvider = ({ children }: { children: React.ReactNode }) => {
  const sessionEndpoints = useEndpoints();
  const organization = sessionEndpoints.organization;
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [allowances, setAllowances] = useState<IntegrationAllowance[]>([]);
  const [sChannels, setSChannels] = useState<SalesChannel[] | null>(null);
  const [fProviders, setFProviders] = useState<FulfillmentProvider[] | null>(null);
  const [generalSubs, setGeneralSubs] = useState<GeneralIntegration[] | null>(null);
  const [subs, setSubs] = useState<IntegrationSubscription[]>([]);
  const [busyFP, setBusyFP] = useState(false);
  const [busySC, setBusySC] = useState(false);
  const [busyGS, setBusyGS] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const isFetchingRef = useRef(false);
  const fetchSequenceRef = useRef(0);

  useEffect(
    () => () => {
      fetchSequenceRef.current += 1;
      isFetchingRef.current = false;
    },
    []
  );

  const refreshIntegrationsData = useCallback(() => {
    if (isFetchingRef.current) return;

    const operation = acquireOperationContext(organization);
    if (!operation.isScopeCurrent()) return;
    const sequence = ++fetchSequenceRef.current;
    const canPublish = () => fetchSequenceRef.current === sequence && operation.isScopeCurrent();

    isFetchingRef.current = true;
    setBusyFP(true);
    setBusySC(true);
    setBusyGS(true);

    void Promise.all([
      sessionEndpoints.integrationData.getIntegrationAllowances(
        {},
        {
          operationRequirement: operationRequirement(operation, 'scope'),
        }
      ),
      sessionEndpoints.integrationData.getIntegrations(
        { limit: 1000 },
        {
          operationRequirement: operationRequirement(operation, 'scope'),
        }
      ),
      sessionEndpoints.integrationData.getIntegrationSubscriptions(
        {},
        {
          operationRequirement: operationRequirement(operation, 'scope'),
        }
      ),
      sessionEndpoints.integrationData.getSalesChannels(
        {},
        {
          operationRequirement: operationRequirement(operation, 'scope'),
        }
      ),
      sessionEndpoints.integrationData.getFulfillmentProviders(
        {},
        {
          operationRequirement: operationRequirement(operation, 'scope'),
        }
      ),
    ])
      .then(([allowanceResponse, integrationResponse, subscriptionResponse, salesChannelResponse, fulfillmentResponse]) => {
        if (!canPublish()) return;

        const nextAllowances = asArray(allowanceResponse.result);
        const nextIntegrations = asArray(integrationResponse.result);
        const nextSubscriptions = asArray(subscriptionResponse.result);
        const salesChannelRecords = asArray(salesChannelResponse.result);
        const fulfillmentRecords = asArray(fulfillmentResponse.result);

        const nextGeneralSubscriptions = nextSubscriptions
          .flatMap((subscription) => {
            const integration = findById(nextIntegrations, subscription.integration_id);
            if (!integration || integration.type !== 3) return [];
            return [
              {
                ...subscription,
                integration_id: subscription.integration_id,
                integration_subscription_id: subscription.id,
                borrowed_icon_url: integration.icon_url,
                borrowed_name: integration.name,
                healthy: subscription.integration_subscription_health?.is_healthy,
              },
            ];
          })
          .sort(byEnabled);

        const nextSalesChannels = salesChannelRecords
          .flatMap((salesChannel) => {
            const subscription = findById(nextSubscriptions, salesChannel.integration_subscription_id);
            const integration = subscription ? findById(nextIntegrations, subscription.integration_id) : undefined;
            if (!subscription || !integration || integration.type !== 1) return [];
            return [
              {
                ...salesChannel,
                enabled: subscription.enabled,
                healthy: subscription.integration_subscription_health?.is_healthy,
                borrowed_icon_url: integration.icon_url,
                borrowed_name: integration.name,
              },
            ];
          })
          .sort(byEnabled);

        const nextFulfillmentProviders = fulfillmentRecords
          .flatMap((fulfillmentProvider) => {
            const subscription = findById(nextSubscriptions, fulfillmentProvider.integration_subscription_id);
            const integration = subscription ? findById(nextIntegrations, subscription.integration_id) : undefined;
            if (!subscription || !integration || integration.type !== 2) return [];
            return [
              {
                ...fulfillmentProvider,
                integration_id: subscription.integration_id,
                enabled: subscription.enabled,
                healthy: subscription.integration_subscription_health?.is_healthy,
                borrowed_icon_url: integration.icon_url,
                borrowed_name: integration.name,
              },
            ];
          })
          .sort(byEnabled);

        setAllowances(nextAllowances);
        setIntegrations(nextIntegrations);
        setSubs(nextSubscriptions);
        setGeneralSubs(nextGeneralSubscriptions);
        setSChannels(nextSalesChannels);
        setFProviders(nextFulfillmentProviders);
      })
      .catch((error: unknown) => {
        if (!canPublish() || isCancellation(error)) return;
        console.error('Error loading integrations data:', error);
        setAllowances([]);
        setIntegrations([]);
        setSubs([]);
        setGeneralSubs([]);
        setSChannels([]);
        setFProviders([]);
      })
      .finally(() => {
        if (!canPublish()) return;
        isFetchingRef.current = false;
        setBusyFP(false);
        setBusySC(false);
        setBusyGS(false);
        setHasFetched(true);
      });
  }, [organization, sessionEndpoints.integrationData]);

  const resetIntegrationsData = useCallback(() => {
    fetchSequenceRef.current += 1;
    isFetchingRef.current = false;
    setIntegrations([]);
    setAllowances([]);
    setSChannels(null);
    setFProviders(null);
    setGeneralSubs(null);
    setSubs([]);
    setBusyFP(false);
    setBusySC(false);
    setBusyGS(false);
    setHasFetched(false);
  }, []);

  return (
    <IntegrationsContext.Provider
      value={{
        integrations,
        allowances,
        sChannels,
        fProviders,
        generalSubs,
        subs,
        busyFP,
        busySC,
        busyGS,
        refreshIntegrationsData,
        hasFetched,
        resetIntegrationsData,
      }}
    >
      {children}
    </IntegrationsContext.Provider>
  );
};

export const useIntegrationsData = () => useContext(IntegrationsContext);
